import { Check } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { NAV_STYLE_OPTIONS, type NavStyle } from '@/components/layout/NavBar/useNavStyle';

interface NavStylePickerProps {
  value: NavStyle;
  onChange: (style: NavStyle) => void;
  className?: string;
}

/** A schematic sidebar row used inside the style preview diagrams. */
function Bar({ className }: { className?: string }) {
  return <div className={cn('h-1.5 shrink-0 rounded-full bg-muted-foreground/25', className)} />;
}

function previewFrame(className?: string) {
  return cn(
    'h-16 w-full overflow-hidden rounded-md border border-border/60 bg-muted/20 p-1.5',
    className,
  );
}

/** A panel sliding in over the top-level list. */
function DrillPreview() {
  return (
    <div className={previewFrame('relative')} aria-hidden="true">
      <div className="flex w-1/2 flex-col gap-1.5 opacity-30">
        <Bar className="w-3/4" />
        <Bar className="w-2/3" />
        <Bar className="w-3/4" />
      </div>
      <div className="absolute inset-y-1.5 right-1.5 flex w-3/5 flex-col gap-1.5 rounded border border-border/60 bg-background p-1.5 shadow-sm">
        <Bar className="w-1/2 bg-primary/60" />
        <Bar className="w-3/4" />
        <Bar className="w-2/3" />
      </div>
    </div>
  );
}

/** A section expanded in place with indented children. */
function ExpandPreview() {
  return (
    <div className={previewFrame('flex flex-col gap-1.5')} aria-hidden="true">
      <Bar className="w-2/3" />
      <div className="ml-2 flex flex-col gap-1.5 border-l border-border/60 pl-2">
        <Bar className="w-1/2 bg-primary/60" />
        <Bar className="w-2/3" />
      </div>
      <Bar className="w-3/5" />
    </div>
  );
}

/** Everything visible under small section labels. */
function FlatPreview() {
  return (
    <div className={previewFrame('flex flex-col gap-1')} aria-hidden="true">
      <div className="h-1 w-1/4 shrink-0 rounded-full bg-muted-foreground/40" />
      <Bar className="w-2/3" />
      <Bar className="w-1/2 bg-primary/60" />
      <div className="mt-0.5 h-1 w-1/3 shrink-0 rounded-full bg-muted-foreground/40" />
      <Bar className="w-3/5" />
    </div>
  );
}

/** A floating panel beside the list. */
function FlyoutPreview() {
  return (
    <div className={previewFrame('relative')} aria-hidden="true">
      <div className="flex w-2/5 flex-col gap-1.5">
        <Bar className="w-full" />
        <Bar className="w-4/5 bg-primary/60" />
        <Bar className="w-full" />
      </div>
      <div className="absolute right-1.5 top-3 flex w-2/5 flex-col gap-1.5 rounded border border-border/60 bg-background p-1.5 shadow-md">
        <Bar className="w-3/4" />
        <Bar className="w-2/3" />
      </div>
    </div>
  );
}

/** Session rows with status dots filling the column, links as a footer strip. */
function SessionsPreview() {
  return (
    <div className={previewFrame('flex flex-col gap-1.5')} aria-hidden="true">
      <div className="flex items-center gap-1">
        <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
        <Bar className="w-2/3 bg-primary/60" />
      </div>
      <div className="flex items-center gap-1">
        <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
        <Bar className="w-1/2" />
      </div>
      <div className="flex items-center gap-1">
        <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
        <Bar className="w-3/5" />
      </div>
      <div className="mt-auto flex gap-1 border-t border-border/60 pt-1">
        <div className="h-1.5 w-1.5 shrink-0 rounded-sm bg-muted-foreground/40" />
        <div className="h-1.5 w-1.5 shrink-0 rounded-sm bg-muted-foreground/40" />
        <div className="h-1.5 w-1.5 shrink-0 rounded-sm bg-muted-foreground/40" />
      </div>
    </div>
  );
}

const PREVIEWS: Record<NavStyle, () => React.JSX.Element> = {
  drill: DrillPreview,
  expand: ExpandPreview,
  flat: FlatPreview,
  flyout: FlyoutPreview,
  sessions: SessionsPreview,
};

/**
 * Radio-card picker for the sidebar navigation style. Each card pairs a
 * schematic preview of the layout with a short description, matching the
 * visual-first selection pattern of the color theme picker.
 */
export function NavStylePicker({ value, onChange, className }: NavStylePickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Navigation style"
      data-testid="nav-style-picker"
      className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}
    >
      {NAV_STYLE_OPTIONS.map((option) => {
        const selected = option.value === value;
        const Preview = PREVIEWS[option.value];
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            data-testid={`nav-style-option-${option.value}`}
            className={cn(
              'group relative flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors',
              selected
                ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50',
            )}
          >
            <Preview />
            <div>
              <div className="text-xs font-medium">{option.label}</div>
              <div className="mt-0.5 text-xs leading-snug text-muted-foreground">
                {option.description}
              </div>
            </div>
            {selected && (
              <div className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
